import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useMeta } from "../lib/meta";
import type { Card, CurrencyBalance, Redemption, RedemptionCapCheck, RedemptionCapStatus, RewardCurrency, SeparateBonusCredit } from "../lib/types";
import { formatINR, formatDate, formatPoints, todayInput, toDateInput } from "../lib/format";
import { Badge, BlockedAction, EmptyState, ErrorBanner, Modal, PageHeader, Panel, ProgressBar, Spinner, StatTile } from "../components/ui";
import { HouseholdPool } from "../components/HouseholdPool";

export default function PointsPage() {
  const meta = useMeta();
  const [balances, setBalances] = useState<CurrencyBalance[] | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [currencies, setCurrencies] = useState<RewardCurrency[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string[]>([]);
  const [caps, setCaps] = useState<RedemptionCapStatus[]>([]);
  const [credits, setCredits] = useState<SeparateBonusCredit[]>([]);
  const [modal, setModal] = useState<{ open: boolean; redemption: Redemption | null }>({ open: false, redemption: null });
  const [capModal, setCapModal] = useState<{ open: boolean; cap: RedemptionCapStatus | null }>({ open: false, cap: null });

  const load = useCallback(async () => {
    try {
      const [b, r, c, cd, rc, sb] = await Promise.all([
        api.get<{ balances: CurrencyBalance[] }>("/points/balances"),
        api.get<{ redemptions: Redemption[] }>("/points/redemptions"),
        api.get<{ currencies: RewardCurrency[] }>("/points/currencies"),
        api.get<{ cards: Card[] }>("/cards"),
        api.get<{ caps: RedemptionCapStatus[] }>("/points/redemption-caps"),
        api.get<{ credits: SeparateBonusCredit[] }>("/points/separate-bonus"),
      ]);
      setCredits(sb.credits);
      setCaps(rc.caps);
      setBalances(b.balances);
      setRedemptions(r.redemptions);
      setCurrencies(c.currencies);
      setCards(cd.cards);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load your points");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function removeCap(cap: RedemptionCapStatus) {
    if (!confirm(`Delete the limit "${cap.name}"? Redemptions already logged are not affected.`)) return;
    try {
      await api.del(`/points/redemption-caps/${cap.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the limit");
    }
  }

  async function remove(r: Redemption) {
    if (!confirm(`Delete the redemption "${r.description}"?`)) return;
    try {
      await api.del(`/points/redemptions/${r.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the redemption");
    }
  }

  if (!meta || !balances) return <Spinner />;

  // A programme with no cards and no history is just catalogue noise here —
  // Settings is where every programme is listed and managed.
  const inUse = balances.filter((b) => b.cardCount > 0 || b.earned > 0 || b.redeemed > 0);

  const totalValue = balances.reduce((s, b) => s + b.value, 0);
  const totalPoints = balances.reduce((s, b) => s + b.balance, 0);
  const totalExpiring = balances.reduce((s, b) => s + b.expiringSoon, 0);
  const totalRedeemedValue = redemptions.reduce((s, r) => s + r.valueReceived, 0);
  const totalRedeemedPoints = redemptions.reduce((s, r) => s + r.pointsUsed, 0);
  const lifetimeRate = totalRedeemedPoints > 0 ? totalRedeemedValue / totalRedeemedPoints : 0;

  return (
    <>
      <PageHeader
        title="Points & redemptions"
        subtitle="Balances with FIFO expiry, and what you actually got for the points you burned."
        actions={
          <BlockedAction blocked={currencies.length === 0} reason="Add a card first — its reward programme comes with it">
            <button className="btn-primary" onClick={() => setModal({ open: true, redemption: null })} disabled={currencies.length === 0}>
              Log a redemption
            </button>
          </BlockedAction>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">Saved, but it goes over a redemption limit:</p>
          <ul className="mt-1 list-disc pl-5">
            {notice.map((n) => <li key={n}>{n}</li>)}
          </ul>
          <button className="mt-1 text-xs underline" onClick={() => setNotice([])}>Dismiss</button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Points on hand"
          value={formatPoints(totalPoints)}
          sub={`Worth ${formatINR(totalValue)} at your everyday valuation`}
        />
        <StatTile
          label={`Expiring in ${meta?.preferences.expiringSoonDays ?? 90} days`}
          value={formatPoints(totalExpiring)}
          sub={totalExpiring > 0 ? "Burn these first" : "Nothing at risk"}
          tone={totalExpiring > 0 ? "warning" : "good"}
        />
        <StatTile label="Redeemed to date" value={formatINR(totalRedeemedValue)} sub={`${formatPoints(totalRedeemedPoints)} points used`} />
        <StatTile
          label="Realised value per point"
          value={lifetimeRate > 0 ? `₹${lifetimeRate.toFixed(2)}` : "—"}
          sub="Across every redemption you've logged"
        />
      </div>

      <div className="mt-6 space-y-6">
        <HouseholdPool />

        <Panel title="Balances by programme" subtitle="Earned minus redeemed, oldest points burned first">
          {inUse.length === 0 ? (
            <EmptyState title="No reward programmes yet" hint="Add a card and its reward currency is created for you." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {inUse.map((b) => (
                <div key={b.currencyId} className="rounded-lg border border-ink-200 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-ink-900">{b.currencyName}</p>
                      <p className="text-xs text-ink-500">
                        ₹{b.valuePerPoint}/pt
                        {b.bestValuePerPoint ? ` · up to ₹${b.bestValuePerPoint}/pt` : ""}
                        {b.expiryMonths ? ` · expires after ${b.expiryMonths} months` : " · never expires"}
                      </p>
                    </div>
                    {b.isCashback && <Badge>Cashback</Badge>}
                  </div>

                  <p className="tnum mt-3 text-2xl font-semibold text-ink-950">{formatPoints(b.balance)}</p>
                  <p className="text-xs text-ink-500">worth {formatINR(b.value)}</p>

                  <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-600">
                    <div><dt className="inline text-ink-400">Earned: </dt><dd className="tnum inline">{formatPoints(b.earned)}</dd></div>
                    <div><dt className="inline text-ink-400">Redeemed: </dt><dd className="tnum inline">{formatPoints(b.redeemed)}</dd></div>
                    {b.transferred > 0 && (
                      <div>
                        <dt className="inline text-ink-400">Transferred out: </dt>
                        <dd className="tnum inline">{formatPoints(b.transferred)}</dd>
                      </div>
                    )}
                  </dl>

                  {b.buckets.length > 0 && (
                    <div className="mt-3 border-t border-ink-100 pt-3">
                      <p className="mb-1.5 text-xs font-medium text-ink-700">Expiry schedule</p>
                      <ul className="space-y-1">
                        {b.buckets.slice(0, 5).map((bucket) => (
                          <li key={bucket.expiresOn} className="flex justify-between text-xs text-ink-600">
                            <span>{formatDate(bucket.expiresOn)}</span>
                            <span className="tnum">{formatPoints(bucket.points)} pts</span>
                          </li>
                        ))}
                      </ul>
                      {b.buckets.length > 5 && (
                        <p className="mt-1 text-xs text-ink-400">+{b.buckets.length - 5} more dates</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        {credits.length > 0 && <SeparateBonusPanel credits={credits} />}

        <Panel
          title="Redemption limits"
          subtitle="How many points a card or programme lets you redeem in a period, whatever the balance"
          actions={
            <BlockedAction blocked={cards.length === 0 && currencies.length === 0} reason="Add a card first">
              <button className="btn-secondary text-sm" onClick={() => setCapModal({ open: true, cap: null })}>
                Add a limit
              </button>
            </BlockedAction>
          }
        >
          {caps.length === 0 ? (
            <EmptyState
              title="No redemption limits"
              hint="Add one where your card has it — HDFC Infinia allows 1,50,000 points a month. Programmes without a limit need nothing here."
            />
          ) : (
            <ul className="space-y-5">
              {caps.map((cap) => {
                const full = cap.remaining <= 0;
                return (
                  <li key={cap.id}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink-900">
                          {cap.cardColor && (
                            <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: cap.cardColor }} aria-hidden />
                          )}
                          {cap.name}
                          <span className="font-normal text-ink-500"> · {cap.cardName ?? `${cap.currencyName}, every card`}</span>
                        </p>
                        <p className="text-xs text-ink-500">
                          {cap.types.length === 0 ? "Every redemption" : cap.types.join(", ")}
                          {cap.includesTransfers ? " · transfers to partners count too" : ""} · {cap.windowLabel} ·{" "}
                          {cap.daysLeft} day{cap.daysLeft === 1 ? "" : "s"} left
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="tnum text-xs text-ink-500">
                          {formatPoints(cap.used)} / {formatPoints(cap.maxPoints)}
                        </span>
                        <button className="text-xs text-accent-700 underline" onClick={() => setCapModal({ open: true, cap })}>Edit</button>
                        <button className="text-xs text-red-700 underline" onClick={() => void removeCap(cap)}>Delete</button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <ProgressBar pct={cap.pct} color={full ? "#b54708" : cap.cardColor ?? undefined} />
                    </div>
                    <p className={`mt-1.5 text-xs ${full ? "text-amber-800" : "text-ink-600"}`}>
                      {full ? (
                        "Limit reached — nothing more can be redeemed against it until it resets."
                      ) : (
                        <>
                          <span className="tnum font-medium text-ink-900">{formatPoints(cap.remaining)}</span> points can
                          still be redeemed
                        </>
                      )}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel title="Redemption history" subtitle="Effective value tells you whether a redemption was worth it">
          {redemptions.length === 0 ? (
            <EmptyState
              title="No redemptions logged"
              hint="Log what you redeem and for how much, and the app works out your real value per point."
              action={
                <button className="btn-primary" onClick={() => setModal({ open: true, redemption: null })} disabled={currencies.length === 0}>
                  Log a redemption
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ink-200">
                    <th className="th">Date</th>
                    <th className="th">What you got</th>
                    <th className="th">Programme</th>
                    <th className="th text-right">Points used</th>
                    <th className="th text-right">Value</th>
                    <th className="th text-right">Per point</th>
                    <th className="th"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {redemptions.map((r) => {
                    const good = r.effectiveValuePerPoint >= r.currency.valuePerPoint;
                    return (
                      <tr key={r.id}>
                        <td className="td whitespace-nowrap text-ink-500">{formatDate(r.date)}</td>
                        <td className="td">
                          <span className="font-medium text-ink-900">{r.description}</span>
                          <span className="block text-xs text-ink-500">{r.type}</span>
                        </td>
                        <td className="td text-ink-600">{r.currency.name}</td>
                        <td className="td tnum text-right">{formatPoints(r.pointsUsed)}</td>
                        <td className="td tnum text-right">{formatINR(r.valueReceived)}</td>
                        <td className={`td tnum text-right font-medium ${good ? "text-emerald-700" : "text-amber-700"}`}>
                          ₹{r.effectiveValuePerPoint.toFixed(2)}
                        </td>
                        <td className="td whitespace-nowrap text-right">
                          <button className="text-xs text-accent-700 underline" onClick={() => setModal({ open: true, redemption: r })}>
                            Edit
                          </button>
                          <button className="ml-3 text-xs text-red-700 underline" onClick={() => remove(r)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-ink-500">
                Green means you beat the everyday rate you set for that programme; amber means you did worse.
              </p>
            </div>
          )}
        </Panel>
      </div>

      <RedemptionModal
        state={modal}
        currencies={currencies}
        cards={cards}
        types={meta.redemptionTypes}
        onClose={() => setModal({ open: false, redemption: null })}
        onSaved={(warnings) => { setModal({ open: false, redemption: null }); setNotice(warnings); void load(); }}
      />
      <RedemptionCapModal
        state={capModal}
        cards={cards}
        currencies={currencies}
        types={meta.redemptionTypes}
        onClose={() => setCapModal({ open: false, cap: null })}
        onSaved={() => { setCapModal({ open: false, cap: null }); void load(); }}
      />
    </>
  );
}

function RedemptionModal({
  state,
  currencies,
  cards,
  types,
  onClose,
  onSaved,
}: {
  state: { open: boolean; redemption: Redemption | null };
  currencies: RewardCurrency[];
  cards: Card[];
  types: string[];
  onClose: () => void;
  onSaved: (capWarnings: string[]) => void;
}) {
  const [form, setForm] = useState({
    currencyId: "", cardId: "", date: todayInput(), pointsUsed: "",
    valueReceived: "", type: "CATALOGUE", description: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const r = state.redemption;
    setForm({
      currencyId: r?.currencyId ?? currencies[0]?.id ?? "",
      cardId: r?.cardId ?? "",
      date: r ? toDateInput(r.date) : todayInput(),
      pointsUsed: r ? String(r.pointsUsed) : "",
      valueReceived: r ? String(r.valueReceived) : "",
      type: r?.type ?? types[0] ?? "Catalogue",
      description: r?.description ?? "",
      notes: r?.notes ?? "",
    });
  }, [state.redemption, state.open, currencies]);

  const [capChecks, setCapChecks] = useState<RedemptionCapCheck[]>([]);

  // What this redemption would leave of any limit it counts against, as it is
  // typed — the point is to know before redeeming, not after.
  useEffect(() => {
    if (!state.open || !form.currencyId) return;
    const timer = setTimeout(() => {
      api
        .post<{ caps: RedemptionCapCheck[] }>("/points/redemption-caps/check", {
          currencyId: form.currencyId,
          cardId: form.cardId || null,
          type: form.type,
          pointsUsed: Number(form.pointsUsed) || 0,
          date: form.date,
          ...(state.redemption ? { excludeId: state.redemption.id } : {}),
        })
        .then((r) => setCapChecks(r.caps))
        .catch(() => setCapChecks([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [state.open, state.redemption, form.currencyId, form.cardId, form.type, form.pointsUsed, form.date]);

  const points = Number(form.pointsUsed);
  const value = Number(form.valueReceived);
  const perPoint = points > 0 && Number.isFinite(value) ? value / points : null;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      currencyId: form.currencyId,
      cardId: form.cardId || null,
      date: form.date,
      pointsUsed: Number(form.pointsUsed),
      valueReceived: Number(form.valueReceived),
      type: form.type,
      description: form.description.trim(),
      notes: form.notes.trim() || null,
    };
    try {
      const r = state.redemption
        ? await api.put<{ capWarnings?: string[] }>(`/points/redemptions/${state.redemption.id}`, payload)
        : await api.post<{ capWarnings?: string[] }>("/points/redemptions", payload);
      onSaved(r.capWarnings ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the redemption");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.redemption ? "Edit redemption" : "Log a redemption"}>
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="r-currency">Programme</label>
            <select id="r-currency" className="input" required value={form.currencyId} onChange={(e) => setForm({ ...form, currencyId: e.target.value })}>
              {currencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="r-card">Card (optional)</label>
            <select
              id="r-card"
              className="input"
              value={form.cardId}
              onChange={(e) => {
                // A card redeems its own programme's points.
                const card = cards.find((c) => c.id === e.target.value);
                setForm({ ...form, cardId: e.target.value, currencyId: card?.currencyId || form.currencyId });
              }}
            >
              <option value="">Not card-specific</option>
              {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="r-date">Date</label>
            <input id="r-date" className="input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="r-type">Redemption type</label>
            <select id="r-type" className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="r-points">Points used</label>
            <input id="r-points" className="input tnum" type="number" min={1} required value={form.pointsUsed} onChange={(e) => setForm({ ...form, pointsUsed: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="r-value">Value received (₹)</label>
            <input id="r-value" className="input tnum" type="number" min={0} step="0.01" required value={form.valueReceived} onChange={(e) => setForm({ ...form, valueReceived: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="r-desc">What you redeemed for</label>
          <input id="r-desc" className="input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. SmartBuy — Delhi–Bengaluru flight" />
        </div>

        <div>
          <label className="label" htmlFor="r-notes">Notes (optional)</label>
          <input id="r-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        {capChecks.length > 0 && (
          <ul className="space-y-1">
            {capChecks.map((c) => (
              <li
                key={c.id}
                className={`rounded-lg border px-3 py-2 text-sm ${
                  c.over ? "border-amber-300 bg-amber-50 text-amber-900" : "border-ink-200 bg-ink-50 text-ink-700"
                }`}
              >
                {c.over ?? (
                  <>
                    <strong>{c.name}:</strong> {formatPoints(c.remainingBefore)} of {formatPoints(c.maxPoints)} points left
                    in {c.windowLabel}
                    {points > 0 && <> — {formatPoints(c.remainingAfter)} after this</>}.
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {perPoint != null && (
          <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-700">
            That's <strong className="tnum">₹{perPoint.toFixed(2)}</strong> per point.
          </p>
        )}

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save redemption"}</button>
        </div>
      </form>
    </Modal>
  );
}

/** Adds or edits a limit on redeeming points: on a card, or on a whole programme. */
function RedemptionCapModal({
  state,
  cards,
  currencies,
  types,
  onClose,
  onSaved,
}: {
  state: { open: boolean; cap: RedemptionCapStatus | null };
  cards: Card[];
  currencies: RewardCurrency[];
  types: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    scope: "CARD" as "CARD" | "PROGRAMME",
    cardId: "",
    currencyId: "",
    name: "",
    maxPoints: "",
    period: "MONTH",
    types: [] as string[],
    includesTransfers: false,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const c = state.cap;
    const firstCard = cards.find((x) => x.isActive) ?? cards[0];
    setForm({
      scope: c?.currencyId ? "PROGRAMME" : "CARD",
      cardId: c?.cardId ?? firstCard?.id ?? "",
      currencyId: c?.currencyId ?? currencies[0]?.id ?? "",
      name: c?.name ?? "",
      maxPoints: c ? String(c.maxPoints) : "",
      period: c?.period ?? "MONTH",
      types: c?.types ?? [],
      includesTransfers: c?.includesTransfers ?? false,
      notes: c?.notes ?? "",
    });
    setError(null);
  }, [state.cap, state.open, cards, currencies]);

  const target =
    form.scope === "CARD" ? cards.find((c) => c.id === form.cardId)?.name : currencies.find((c) => c.id === form.currencyId)?.name;
  const periodWord = { MONTH: "monthly", STATEMENT: "per-statement", QUARTER: "quarterly", YEAR: "yearly" }[form.period] ?? "monthly";

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      cardId: form.scope === "CARD" ? form.cardId : null,
      currencyId: form.scope === "PROGRAMME" ? form.currencyId : null,
      // A limit is easier to find by what it limits than by a name made up for it.
      name: form.name.trim() || `${target ?? "Redemption"} ${periodWord} limit`,
      maxPoints: Number(form.maxPoints),
      period: form.period,
      types: form.types,
      includesTransfers: form.includesTransfers,
      notes: form.notes.trim() || null,
    };
    try {
      if (state.cap) await api.put(`/points/redemption-caps/${state.cap.id}`, payload);
      else await api.post("/points/redemption-caps", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the limit");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.cap ? "Edit redemption limit" : "Add a redemption limit"}>
      <form onSubmit={save} className="space-y-4">
        <fieldset>
          <legend className="label mb-1">The limit is on</legend>
          <div className="flex flex-wrap gap-4 text-sm text-ink-700">
            <label className="flex items-center gap-2">
              <input type="radio" name="rc-scope" checked={form.scope === "CARD"} onChange={() => setForm({ ...form, scope: "CARD" })} />
              One card
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="rc-scope" checked={form.scope === "PROGRAMME"} onChange={() => setForm({ ...form, scope: "PROGRAMME" })} />
              A whole programme, every card
            </label>
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          {form.scope === "CARD" ? (
            <div>
              <label className="label" htmlFor="rc-card">Card</label>
              <select id="rc-card" className="input" required value={form.cardId} onChange={(e) => setForm({ ...form, cardId: e.target.value })}>
                {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="label" htmlFor="rc-currency">Programme</label>
              <select id="rc-currency" className="input" required value={form.currencyId} onChange={(e) => setForm({ ...form, currencyId: e.target.value })}>
                {currencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label" htmlFor="rc-name">Name (optional)</label>
            <input id="rc-name" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={`${target ?? "Card"} ${periodWord} limit`} />
          </div>
          <div>
            <label className="label" htmlFor="rc-max">At most (points)</label>
            <input id="rc-max" className="input tnum" type="number" min={1} required value={form.maxPoints} onChange={(e) => setForm({ ...form, maxPoints: e.target.value })} placeholder="150000" />
          </div>
          <div>
            <label className="label" htmlFor="rc-period">Every</label>
            <select id="rc-period" className="input" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}>
              <option value="MONTH">Calendar month</option>
              <option value="STATEMENT" disabled={form.scope !== "CARD"}>Statement cycle</option>
              <option value="QUARTER">Quarter</option>
              <option value="YEAR">Year</option>
            </select>
          </div>
        </div>

        <div>
          <span className="label">Which redemptions it covers</span>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => {
              const on = form.types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={on}
                  className={`chip-action ring-1 ${on ? "bg-accent-50 text-accent-800 ring-accent-200" : "bg-white text-ink-600 ring-ink-200 hover:bg-ink-50"}`}
                  onClick={() => setForm({ ...form, types: on ? form.types.filter((x) => x !== t) : [...form.types, t] })}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-ink-500">
            {form.types.length === 0
              ? "None picked: every redemption counts. Pick types for a limit on only some kinds — flights and hotels, say."
              : `Only ${form.types.join(", ")} redemptions count.`}
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.includesTransfers}
            onChange={(e) => setForm({ ...form, includesTransfers: e.target.checked })}
          />
          <span>
            Points transferred to airline and hotel partners count too
            <span className="block text-xs text-ink-500">
              Tick this if the bank counts transfers towards the same limit. Transfers you log under Miles &amp; transfers
              are then included.
            </span>
          </span>
        </label>

        <div>
          <label className="label" htmlFor="rc-notes">Notes (optional)</label>
          <input id="rc-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save limit"}</button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Accelerated points an issuer credits after the base points, by the day they
 * are due — so a balance that looks short on the bank's side is explained,
 * and a credit that never came can be chased.
 */
function SeparateBonusPanel({ credits }: { credits: SeparateBonusCredit[] }) {
  const upcoming = credits.filter((c) => !c.due);
  const due = credits.filter((c) => c.due);
  const row = (c: SeparateBonusCredit) => (
    <li key={`${c.cardId}:${c.dueOn}`} className="flex items-start justify-between gap-3 py-2 text-sm">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-medium text-ink-900">
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: c.cardColor }} aria-hidden />
          {c.cardName}
        </p>
        <p className="text-xs text-ink-500">
          On {c.spends} spend{c.spends === 1 ? "" : "s"}, {formatDate(c.from)}
          {c.to !== c.from ? ` – ${formatDate(c.to)}` : ""}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="tnum font-medium text-ink-900">{formatPoints(c.points)} pts</p>
        <p className={`text-xs ${c.due ? "text-amber-700" : "text-ink-500"}`}>
          {c.due ? "due " : "expected "}
          {formatDate(c.dueOn)}
        </p>
      </div>
    </li>
  );
  return (
    <Panel
      title="Accelerated points credited separately"
      subtitle="Some issuers credit the extra over the base rate later — Axis credits Magnus's 45 days after the month. These are counted in your balances already."
    >
      {upcoming.length > 0 && (
        <>
          <p className="label mb-1">Still to come</p>
          <ul className="divide-y divide-ink-100">{upcoming.map(row)}</ul>
        </>
      )}
      {due.length > 0 && (
        <>
          <p className={`label mb-1 ${upcoming.length > 0 ? "mt-4" : ""}`}>Due in the last 60 days — check they arrived</p>
          <ul className="divide-y divide-ink-100">{due.map(row)}</ul>
        </>
      )}
    </Panel>
  );
}
